import dayjs from 'dayjs';

import type { Booking, BookingList, BookingStage, GarmentState } from '../api/bookings';
import { formatDate, formatDateTime } from '../format/dates';
import { formatAmount } from '../format/money';
import { depositBlock, liveGarments } from '../print/customerMoney';
import { readyTick } from './garmentMoves';
import { referenceLabel } from './reference';
import { dayAfter } from './registerViews';
import { isOutOnRent } from './tones';

/**
 * The phone's Bookings tab as three lists, by where the garments are (owner, 2026-10-14).
 *
 * <p>
 * <strong>Why it changed.</strong> The tab opened on "All", sorted by pickup date from the
 * earliest, so the first rows were bookings finished months ago, cancelled ones and old enquiries;
 * seven chips scrolled sideways, four of them repeating the Today tab; and every row ended in a
 * system word ("Confirmed", "In progress"). Now: Upcoming, With customers, Done — each a server
 * list (`BookingList`) that filters and orders — under day headings, each row led by the one date
 * that matters to it, and a word on the right only when something needs attention.
 * </p>
 */

/**
 * Short enough to sit three across with their counts (2026-10-15: "With customers" was cut off on
 * a phone). The server's names are unchanged.
 */
export const bookingLists: readonly { id: BookingList; label: string }[] = [
  { id: 'Upcoming', label: 'Coming' },
  { id: 'WithCustomers', label: 'Out' },
  { id: 'Done', label: 'Done' },
];

/** Which list a booking belongs to — for search results, which look across all three. */
export function listOf(stage: BookingStage): BookingList {
  switch (stage) {
    case 'InProgress':
    case 'Recovery':
      return 'WithCustomers';
    case 'Completed':
    case 'Cancelled':
    case 'Expired':
      return 'Done';
    default:
      return 'Upcoming';
  }
}

export type FlagTone = 'danger' | 'warning' | 'muted';

export interface ListRow {
  /** The date the tile shows: the pickup for what is coming, the return for the rest. */
  date: string;
  /** The tile's two lines — "18", "OCT". */
  day: string;
  month: string;
  /** One line under the name. */
  line: string;
  /** A word on the right only when it needs attention — late, on hold, a quote. */
  flag: { text: string; tone: FlagTone } | null;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** What is on the booking, by design: "Sherwani, Safa", "Sherwani and 2 more". */
export function garmentWords(booking: Booking): string {
  const names = [...new Set(liveGarments(booking).map((garment) => garment.itemName))];

  if (names.length === 0) {
    return 'No garments';
  }

  return names.length <= 2 ? names.join(', ') : `${names[0]!} and ${names.length - 1} more`;
}

export function listRow(booking: Booking, list: BookingList, today: string): ListRow {
  const live = liveGarments(booking);
  const out = live.filter((garment) => isOutOnRent(garment.state));
  const what = garmentWords(booking);
  const date = list === 'Upcoming' ? booking.possessionFromDate : booking.possessionToDate;
  const tile = dayjs(date);
  const base = { date, day: tile.format('D'), month: tile.format('MMM').toUpperCase() };

  if (list === 'Upcoming') {
    switch (booking.stage) {
      case 'Hold':
        return {
          ...base,
          line:
            booking.holdExpiresOn && !booking.isHoldExpired
              ? `Hold ends ${formatDateTime(booking.holdExpiresOn)} · ${what}`
              : `The hold has lapsed · ${what}`,
          flag: { text: 'On hold', tone: 'warning' },
        };
      case 'Enquiry':
      case 'Quotation':
        return {
          ...base,
          line: `${referenceLabel(booking)} · ${what}`,
          flag: { text: booking.stage === 'Quotation' ? 'Quote' : 'Enquiry', tone: 'muted' },
        };
      default:
        break;
    }

    if (date < today) {
      return {
        ...base,
        line: `Was to collect ${formatDate(date)} · ${what}`,
        flag: { text: 'Not collected', tone: 'warning' },
      };
    }

    const when =
      date === today ? 'Picks up today · ' : date === dayAfter(today) ? 'Picks up tomorrow · ' : '';

    return { ...base, line: `${when}${what}`, flag: null };
  }

  if (list === 'WithCustomers') {
    if (booking.stage === 'Recovery') {
      return {
        ...base,
        line: `Not returned · ${what}`,
        flag: { text: 'Recovery', tone: 'danger' },
      };
    }

    const garments = plural(out.length, 'garment', 'garments');

    if (booking.isOverdue) {
      return {
        ...base,
        line: `Was due back ${formatDate(date)} · ${garments} out`,
        flag: {
          text: `${plural(Math.max(1, booking.daysOverdue), 'day', 'days')} late`,
          tone: 'danger',
        },
      };
    }

    return {
      ...base,
      line: `${date === today ? 'Back today · ' : ''}${garments} out · ${what}`,
      flag: null,
    };
  }

  // Done.
  switch (booking.stage) {
    case 'Cancelled':
      return { ...base, line: what, flag: { text: 'Cancelled', tone: 'muted' } };
    case 'Expired':
      return { ...base, line: what, flag: { text: 'Expired', tone: 'muted' } };
    default:
      return { ...base, line: `Returned · ${what}`, flag: null };
  }
}

export interface DayGroup<T> {
  key: string;
  title: string;
  /** Said in red with a clock — the late group only. */
  late: boolean;
  /**
   * Every row in it is on one day, which the heading names — so a row leads with the person, not a
   * date tile. False for "Later", "Not collected" and Done's months, whose rows differ in date.
   */
  dated: boolean;
  items: T[];
}

/**
 * The day headings, in the order the server sent the rows (each list's own order).
 *
 * <p>
 * Upcoming: "Not collected" for pickups already gone, then Today, Tomorrow, "Sat 25 Oct".
 * With customers: Late, then "Back today", "Back tomorrow", "Back Sat 25 Oct". Done: by month.
 * </p>
 */
export function groupByDay<T>(
  items: readonly T[],
  rowOf: (item: T) => ListRow,
  list: BookingList,
  today: string,
  /** Rows after this day go under one "Later" heading — the end of the week the strip shows. */
  { laterAfter }: { laterAfter?: string } = {},
): DayGroup<T>[] {
  const tomorrow = dayAfter(today);
  // By key, in the order each key first appears — so one heading per group even where the server's
  // order interleaves two kinds of row on the same side of today.
  const groups = new Map<string, DayGroup<T>>();

  for (const item of items) {
    const { date, flag } = rowOf(item);
    let key: string;
    let title: string;
    let late = false;
    let dated = false;

    if (list === 'Done') {
      key = date.slice(0, 7);
      title = dayjs(date).format('MMMM YYYY');
    } else if (list === 'WithCustomers' && flag?.tone === 'danger') {
      key = 'late';
      title = 'Late';
      late = true;
    } else if (list === 'Upcoming' && date < today) {
      // A pickup day already gone — a confirmed booking nobody collected, or a hold left standing.
      key = 'missed';
      title = 'Not collected';
    } else if (laterAfter !== undefined && date > laterAfter) {
      key = 'later';
      title = 'Later';
    } else {
      const out = list === 'WithCustomers';
      const named = dayjs(date).format('ddd D MMM');
      key = date;
      dated = true;
      title =
        date === today
          ? `${out ? 'Back today' : 'Picks up today'} · ${named}`
          : date === tomorrow
            ? `${out ? 'Back tomorrow' : 'Tomorrow'} · ${named}`
            : `${out ? 'Back ' : ''}${named}`;
    }

    const group = groups.get(key);

    if (group) {
      group.items.push(item);
    } else {
      groups.set(key, { key, title, late, dated, items: [item] });
    }
  }

  return [...groups.values()];
}

/* ------------------------------------------------- the board (B with C, 2026-10-15) */

/** The day a list reads a booking by: when it goes out (Coming), when it is due back (Out, Done). */
export function listDay(
  booking: Pick<Booking, 'possessionFromDate' | 'possessionToDate'>,
  list: BookingList,
): string {
  return list === 'Upcoming' ? booking.possessionFromDate : booking.possessionToDate;
}

/** The seven days of the week holding `day`, Monday first — counted by hand, not by the locale. */
export function weekOf(day: string): string[] {
  const date = dayjs(day);
  const monday = date.subtract((date.day() + 6) % 7, 'day');

  return Array.from({ length: 7 }, (_, index) => monday.add(index, 'day').format('YYYY-MM-DD'));
}

export interface StripDay {
  date: string;
  /** "Mon" */
  weekday: string;
  /** "12" */
  day: string;
  count: number;
  today: boolean;
  /** Already gone — drawn quietly on Coming, where nothing can still go out that day. */
  past: boolean;
  /** Out only: a day gone with something still not back. */
  late: boolean;
}

/**
 * The week strip: how many go out (Coming) or come back (Out) each day of the week shown, from the
 * server's own list for that week — never from a page that happens to be loaded.
 */
export function weekStrip(
  week: readonly string[],
  bookings: readonly Pick<Booking, 'possessionFromDate' | 'possessionToDate'>[],
  list: BookingList,
  today: string,
): StripDay[] {
  return week.map((date) => {
    const count = bookings.filter((booking) => listDay(booking, list) === date).length;
    const shown = dayjs(date);

    return {
      date,
      weekday: shown.format('ddd'),
      day: shown.format('D'),
      count,
      today: date === today,
      past: date < today,
      late: list === 'WithCustomers' && date < today && count > 0,
    };
  });
}

/** "Coming back this week · 12 – 18 Oct", "Going out · 26 Oct – 1 Nov". */
export function weekLabel(week: readonly string[], list: BookingList, today: string): string {
  const first = dayjs(week[0]);
  const last = dayjs(week[week.length - 1]);
  const verb = list === 'Upcoming' ? 'Going out' : 'Coming back';
  const range =
    first.month() === last.month()
      ? `${first.format('D')} – ${last.format('D MMM')}`
      : `${first.format('D MMM')} – ${last.format('D MMM')}`;

  return `${verb}${week.includes(today) ? ' this week' : ''} · ${range}`;
}

export type RowAction = 'call' | 'takeBack' | 'handOver' | 'markReady';

export interface RowDetail {
  /** The money in one line of two halves — "Late fee so far ₹5,400" · "Deposit ₹13,950 held". */
  money: { left: string; right: string | null } | null;
  /** The buttons under it, the next step last (it is drawn filled). */
  actions: RowAction[];
}

/** The garments a "Mark ready" press moves to Ready — the Today tab's own tick, for every one. */
export function markReadyMoves(
  booking: Pick<Booking, 'wearers'>,
): { garmentId: number; state: GarmentState }[] {
  return liveGarments(booking as Booking).flatMap((garment) => {
    const tick = readyTick(garment.state);

    return tick && !tick.ticked ? [{ garmentId: garment.id, state: tick.next }] : [];
  });
}

/**
 * What a row says about money and what it offers to do, by where the booking is (owner, 2026-10-15:
 * "B" — most bookings dealt with from the list).
 *
 * <p>
 * Out: the money always (a late fee so far, or what is owed, and the deposit held); Call and Take
 * back once it is due back today or late. Coming: what to collect at pickup for today and tomorrow;
 * Hand over on the day, Mark ready the day before. Anything later is a quiet row.
 * </p>
 */
export function rowDetail(
  booking: Booking,
  list: BookingList,
  today: string,
  currency = '₹',
): RowDetail {
  const cash = (amount: number) => `${currency}${formatAmount(amount)}`;
  const deposit = depositBlock(booking);

  if (list === 'WithCustomers') {
    const left =
      booking.isOverdue && (booking.suggestedLateFee ?? 0) > 0
        ? `Late fee so far ${cash(booking.suggestedLateFee ?? 0)}`
        : booking.balanceDue > 0
          ? `${cash(booking.balanceDue)} owed`
          : 'Nothing owed';
    const due = booking.isOverdue || booking.possessionToDate <= today;

    return {
      money: { left, right: deposit.held > 0 ? `Deposit ${cash(deposit.held)} held` : null },
      actions: booking.stage === 'Recovery' ? ['call'] : due ? ['call', 'takeBack'] : [],
    };
  }

  const tomorrow = dayAfter(today);

  if (
    list === 'Upcoming' &&
    booking.stage === 'Confirmed' &&
    booking.possessionFromDate <= tomorrow
  ) {
    const onTheDay = booking.possessionFromDate <= today;

    return {
      money: {
        left: `To collect ${cash(Math.max(0, booking.balanceDue))}`,
        right: deposit.due > 0 ? `+ deposit ${cash(deposit.due)}` : null,
      },
      actions: onTheDay
        ? ['call', 'handOver']
        : markReadyMoves(booking).length > 0
          ? ['call', 'markReady']
          : ['call'],
    };
  }

  return { money: null, actions: [] };
}

export interface LateBanner {
  count: number;
  /** The one to chase first — the longest out. */
  first: Booking;
  title: string;
  line: string;
}

/** The red banner over Out: one late booking in full, or how many and the longest. */
export function lateBanner(overdue: readonly Booking[], currency = '₹'): LateBanner | null {
  const late = overdue.filter((booking) => booking.isOverdue);

  if (late.length === 0) {
    return null;
  }

  const first = [...late].sort((a, b) => b.daysOverdue - a.daysOverdue)[0]!;
  const days = plural(Math.max(1, first.daysOverdue), 'day', 'days');
  const cash = (amount: number) => `${currency}${formatAmount(amount)}`;

  if (late.length === 1) {
    const held = depositBlock(first).held;
    const parts = [
      garmentWords(first),
      (first.suggestedLateFee ?? 0) > 0
        ? `late fee so far ${cash(first.suggestedLateFee ?? 0)}`
        : '',
      held > 0 ? `deposit ${cash(held)} held` : '',
    ].filter(Boolean);

    return {
      count: 1,
      first,
      title: `Late · ${days} — ${first.customerName}`,
      line: parts.join(' · '),
    };
  }

  return {
    count: late.length,
    first,
    title: `${late.length} late`,
    line: `Longest: ${first.customerName}, ${days}`,
  };
}

/**
 * A row's second line under a day heading — what is on it, never the day again (the heading says
 * it): "Classic Navy Suit · 1 garment" for what is out, the booking's number for what is coming.
 */
export function boardLine(booking: Booking, list: BookingList): string {
  const what = garmentWords(booking);

  if (list === 'WithCustomers') {
    const out = liveGarments(booking).filter((garment) => isOutOnRent(garment.state)).length;

    return `${what} · ${plural(Math.max(out, 1), 'garment', 'garments')}`;
  }

  return `${what} · ${referenceLabel(booking)}`;
}
